import { useState } from 'react';
import { Box, IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { FilterAltOff, Search } from '@mui/icons-material';
import {
  CustomTextField,
  GeneralTableBox,
  HorizontalForm,
  InfoTooltip,
  NoDataSkeleton,
  RowTableCell,
  SortableHeadTableCell,
} from '../../../common/components';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useDebouncedValue } from '../../../common/hooks/useDebouncedValue';
import { sortRows } from '../../../utils/sortRows';
import { CONSOLIDATED_SUPPLY_TEXTS as TEXTS } from '../constants/consolidatedSupplyTexts';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
/** Expone cada pedido omitido y su motivo sin interrumpir la preparación de los demás. */
const ConsolidatedSupplyExclusions = ({ rows }) => {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search).trim().toLowerCase();
  const { sort, toggleSort, resetSort } = useTableSort();
  const visible = sortRows(
    rows.filter((row) => `${row.order_number} ${row.reason}`.toLowerCase().includes(query)),
    sort
  );
  return (
    <Box>
      <HorizontalForm withBorder={false} sx={{ px: 2, py: 1.5, gap: 1, alignItems: 'center' }}>
        <CustomTextField
          label={TEXTS.searchExcluded}
          value={search}
          sx={{ flex: '1 1 260px', minWidth: 0, mt: 0 }}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search sx={{ color: 'primary.main', mr: 1 }} /> }}
        />
        <Tooltip title={TEXTS.clearFilter}>
          <IconButton
            color="primary"
            aria-label={TEXTS.clearFilter}
            onClick={() => {
              setSearch('');
              resetSort();
            }}
          >
            <FilterAltOff />
          </IconButton>
        </Tooltip>
        <InfoTooltip lines={[TEXTS.excludedHint]} />
      </HorizontalForm>
      <ClientPaginatedTable rows={visible} resetKey={`${query}:${sort?.key}:${sort?.direction}`}>
        {(pageRows) => (
          <GeneralTableBox fixedLayout minTableWidth={550} hasMaxHeight={false}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <SortableHeadTableCell
                    sortKey="order_number"
                    sort={sort}
                    onSort={toggleSort}
                    sx={{ width: 130 }}
                  >
                    {TEXTS.order}
                  </SortableHeadTableCell>
                  <SortableHeadTableCell sortKey="reason" sort={sort} onSort={toggleSort}>
                    {TEXTS.reason}
                  </SortableHeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow hover key={row.uuid}>
                    <RowTableCell sx={{ textAlign: 'left', fontSize: 13 }} title={row.order_number}>
                      {row.order_number}
                    </RowTableCell>
                    <RowTableCell
                      sx={{
                        textAlign: 'left',
                        fontSize: 13,
                        py: 1.5,
                        '&&': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
                      }}
                    >
                      {row.reason}
                    </RowTableCell>
                  </TableRow>
                ))}
                {!visible.length && (
                  <NoDataSkeleton
                    colSpan={2}
                    showSubtitle={false}
                    message={query ? TEXTS.noMatches : TEXTS.excludedEmpty}
                  />
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        )}
      </ClientPaginatedTable>
    </Box>
  );
};

export default ConsolidatedSupplyExclusions;
